import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format, parseISO, differenceInMinutes, addDays, subDays } from 'date-fns';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
  Truck, Clock, MapPin, User, Navigation, 
  AlertCircle, CheckCircle2, RefreshCw, Maximize,
  ChevronLeft, ChevronRight, Home, Calendar,
  ArrowRight, Timer
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Kent, WA office coordinates
const OFFICE_COORDS = [47.3826, -122.2171];

// Custom marker icons
const createIcon = (color, isNext = false) => {
  const size = isNext ? 40 : 30;
  const className = isNext ? 'pulse-marker' : '';
  return L.divIcon({
    className: `custom-marker ${className}`,
    html: `<div style="
      background-color: ${color};
      width: ${size}px;
      height: ${size}px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 10px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: ${isNext ? '16px' : '12px'};
    "><svg xmlns="http://www.w3.org/2000/svg" width="${isNext ? 20 : 14}" height="${isNext ? 20 : 14}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg></div>`,
    iconSize: [size, size],
    iconAnchor: [size/2, size/2],
  });
};

const officeIcon = L.divIcon({
  className: 'office-marker',
  html: `<div style="
    background-color: #3b82f6;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 2px 10px rgba(0,0,0,0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
  "><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg></div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
});

// Component to fit map bounds
const FitBounds = ({ runs, officeCoords }) => {
  const map = useMap();
  
  useEffect(() => {
    const coords = [officeCoords];
    runs.forEach(run => {
      if (run.location_lat && run.location_lon) {
        coords.push([run.location_lat, run.location_lon]);
      }
    });
    
    if (coords.length > 1) {
      const bounds = L.latLngBounds(coords);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [runs, officeCoords, map]);
  
  return null;
};

const DailyDispatchDisplay = () => {
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [dayData, setDayData] = useState(null);
  const [weather, setWeather] = useState(null);
  const [travelTimes, setTravelTimes] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedRunId, setSelectedRunId] = useState(null);

  // Format time from 24h to 12h
  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  // Calculate "Leave By" time
  const calculateLeaveBy = (runTime, runDate, travelMinutes) => {
    if (!runTime || !travelMinutes) return null;
    
    const [hours, minutes] = runTime.split(':').map(Number);
    const [year, month, day] = runDate.split('-').map(Number);
    const runDateTime = new Date(year, month - 1, day, hours, minutes, 0);
    
    const leaveByTime = new Date(runDateTime.getTime() - (travelMinutes * 60 * 1000));
    const now = new Date();
    
    const minutesUntilLeave = differenceInMinutes(leaveByTime, now);
    
    return {
      time: format(leaveByTime, 'h:mm a'),
      minutesUntil: minutesUntilLeave,
      isPast: minutesUntilLeave < 0,
      isUrgent: minutesUntilLeave >= 0 && minutesUntilLeave <= 30,
      isSoon: minutesUntilLeave > 30 && minutesUntilLeave <= 60
    };
  };

  // Find the next upcoming run
  const nextRun = useMemo(() => {
    if (!dayData?.trucking) return null;
    const now = new Date();
    const todayStr = format(now, 'yyyy-MM-dd');
    
    if (selectedDate !== todayStr) return null;
    
    const currentTimeStr = format(now, 'HH:mm');
    return dayData.trucking.find(run => run.time > currentTimeStr) || null;
  }, [dayData, selectedDate, currentTime]);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      // Calculate which week offset contains the selected date
      // Week starts on Sunday, matching backend calculation
      const selectedDateObj = parseISO(selectedDate);
      const today = new Date();
      
      // Find Sunday of current week (today's week start)
      const daysSinceSunday = today.getDay(); // 0=Sunday, 1=Monday, etc.
      const currentWeekSunday = new Date(today);
      currentWeekSunday.setDate(today.getDate() - daysSinceSunday);
      currentWeekSunday.setHours(0, 0, 0, 0);
      
      // Find Sunday of selected date's week
      const selectedDayOfWeek = selectedDateObj.getDay();
      const selectedWeekSunday = new Date(selectedDateObj);
      selectedWeekSunday.setDate(selectedDateObj.getDate() - selectedDayOfWeek);
      selectedWeekSunday.setHours(0, 0, 0, 0);
      
      // Calculate week offset as difference in weeks between the two Sundays
      const msPerWeek = 7 * 24 * 60 * 60 * 1000;
      const weekOffset = Math.round((selectedWeekSunday - currentWeekSunday) / msPerWeek);
      
      const [weekResponse, weatherResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dispatch/week-data?week_offset=${weekOffset}`),
        axios.get(`${BACKEND_URL}/api/dispatch/weather`)
      ]);
      
      // Find the selected day's data
      const dayInfo = weekResponse.data.days.find(d => d.date === selectedDate);
      setDayData(dayInfo || { date: selectedDate, trucking: [], total_runs: 0 });
      
      // Get weather for selected date
      const dayWeather = weatherResponse.data.forecast?.find(w => w.date === selectedDate);
      setWeather(dayWeather);
      
      setLastUpdated(new Date());
      
      // Fetch travel times for runs with coordinates
      if (dayInfo?.trucking) {
        const runsWithCoords = dayInfo.trucking.filter(run => run.location_lat && run.location_lon);
        
        const travelTimePromises = runsWithCoords.map(async (run) => {
          const key = `${run.location_lat},${run.location_lon}`;
          if (travelTimes[key]) return { key, data: travelTimes[key] };
          
          try {
            const response = await axios.get(`${BACKEND_URL}/api/dispatch/travel-time?dest_lat=${run.location_lat}&dest_lon=${run.location_lon}`);
            return { key, data: response.data };
          } catch (e) {
            return { key, data: null };
          }
        });
        
        const travelTimeResults = await Promise.all(travelTimePromises);
        const newTravelTimes = { ...travelTimes };
        travelTimeResults.forEach(({ key, data }) => {
          if (data) newTravelTimes[key] = data;
        });
        setTravelTimes(newTravelTimes);
      }
      
    } catch (error) {
      console.error('Failed to fetch dispatch data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  // Initial load and auto-refresh
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [fetchData]);

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  // Navigate days
  const goToPreviousDay = () => {
    const prev = subDays(parseISO(selectedDate), 1);
    setSelectedDate(format(prev, 'yyyy-MM-dd'));
  };

  const goToNextDay = () => {
    const next = addDays(parseISO(selectedDate), 1);
    setSelectedDate(format(next, 'yyyy-MM-dd'));
  };

  const goToToday = () => {
    setSelectedDate(format(new Date(), 'yyyy-MM-dd'));
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Get marker color based on status and if it's next
  const getMarkerColor = (run, isNextRun) => {
    if (isNextRun) return '#f97316'; // Orange for next
    if (run.status === 'Confirmed') return '#22c55e'; // Green
    return '#eab308'; // Yellow for tentative
  };

  const isToday = selectedDate === format(new Date(), 'yyyy-MM-dd');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400 text-xl">Loading Daily Dispatch...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-800">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Truck className="h-8 w-8 text-orange-500" />
            <h1 className="text-2xl font-bold">Daily Dispatch</h1>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {/* View Toggle */}
          <div className="flex items-center gap-1 bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => window.location.href = '/dispatch'}
              className="px-3 py-2 rounded-md hover:bg-gray-700 text-sm transition-colors"
            >
              Week
            </button>
            <button
              className="px-3 py-2 rounded-md bg-orange-500 text-white text-sm font-medium"
            >
              Day
            </button>
          </div>
          
          {/* Date Navigation */}
          <div className="flex items-center gap-2 bg-gray-800 rounded-lg p-1">
            <button
              onClick={goToPreviousDay}
              className="p-2 rounded hover:bg-gray-700 transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div className="px-4 py-2 min-w-[200px] text-center">
              <div className="font-semibold">
                {format(parseISO(selectedDate), 'EEEE')}
              </div>
              <div className="text-sm text-gray-400">
                {format(parseISO(selectedDate), 'MMMM d, yyyy')}
              </div>
            </div>
            <button
              onClick={goToNextDay}
              className="p-2 rounded hover:bg-gray-700 transition-colors"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          
          <button
            onClick={goToToday}
            className={`px-4 py-2 rounded-lg transition-colors ${
              isToday ? 'bg-orange-500 text-white' : 'bg-gray-800 hover:bg-gray-700'
            }`}
          >
            Today
          </button>
          
          {/* Weather */}
          {weather && (
            <div className="flex items-center gap-2 bg-gray-800 rounded-lg px-4 py-2">
              <img 
                src={`https://openweathermap.org/img/wn/${weather.icon}@2x.png`}
                alt={weather.condition}
                className="h-10 w-10 -my-2"
              />
              <div>
                <div className="font-medium">{Math.round(weather.temp_high)}°F</div>
                <div className="text-xs text-gray-400">{Math.round(weather.temp_low)}°F</div>
              </div>
            </div>
          )}
          
          {/* Current Time */}
          <div className="text-right">
            <div className="text-2xl font-mono text-orange-400">
              {format(currentTime, 'h:mm a')}
            </div>
          </div>
          
          {/* Fullscreen & Refresh */}
          <div className="flex items-center gap-2">
            <button
              onClick={toggleFullscreen}
              className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors"
              title="Toggle Fullscreen"
            >
              <Maximize className="h-5 w-5" />
            </button>
            {lastUpdated && (
              <div className="text-xs text-gray-500 flex items-center gap-1">
                <RefreshCw className="h-3 w-3" />
                {format(lastUpdated, 'h:mm:ss a')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content - Split View */}
      <div className="flex-1 flex overflow-hidden">
        {/* Map Panel - Left Side */}
        <div className="w-1/2 relative">
          <MapContainer
            center={OFFICE_COORDS}
            zoom={10}
            style={{ height: '100%', width: '100%' }}
            className="z-0"
          >
            <TileLayer
              attribution='&copy; <a href="https://carto.com/">CARTO</a>'
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            />
            
            {/* Office Marker */}
            <Marker position={OFFICE_COORDS} icon={officeIcon}>
              <Popup>
                <div className="text-gray-900 font-medium">
                  <Home className="h-4 w-4 inline mr-1" />
                  AV Factory - Kent Office
                </div>
              </Popup>
            </Marker>
            
            {/* Run Markers */}
            {dayData?.trucking?.map((run, index) => {
              if (!run.location_lat || !run.location_lon) return null;
              const isNextRun = nextRun?.id === run.id;
              const isSelected = selectedRunId === run.id;
              
              return (
                <Marker
                  key={run.id}
                  position={[run.location_lat, run.location_lon]}
                  icon={createIcon(getMarkerColor(run, isNextRun), isNextRun || isSelected)}
                  eventHandlers={{
                    click: () => setSelectedRunId(run.id)
                  }}
                >
                  <Popup>
                    <div className="text-gray-900 min-w-[200px]">
                      <div className="font-bold">{formatTime(run.time)}</div>
                      <div className="font-medium">{run.event_name}</div>
                      <div className="text-sm text-gray-600">{run.action}</div>
                      <div className="text-sm mt-1">
                        <MapPin className="h-3 w-3 inline mr-1" />
                        {run.location_name}
                      </div>
                      {run.truck_assignments?.[0] && (
                        <div className="text-sm">
                          <User className="h-3 w-3 inline mr-1" />
                          {run.truck_assignments[0].driver_name}
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              );
            })}
            
            <FitBounds runs={dayData?.trucking || []} officeCoords={OFFICE_COORDS} />
          </MapContainer>
          
          {/* Map Legend */}
          <div className="absolute bottom-4 left-4 bg-gray-900/90 backdrop-blur rounded-lg p-3 text-sm z-[1000]">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-4 h-4 rounded-full bg-blue-500 border-2 border-white"></div>
              <span>Office</span>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-4 h-4 rounded-full bg-orange-500 border-2 border-white"></div>
              <span>Next Up</span>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-4 h-4 rounded-full bg-green-500 border-2 border-white"></div>
              <span>Confirmed</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-yellow-500 border-2 border-white"></div>
              <span>Tentative</span>
            </div>
          </div>
          
          {/* Stats Overlay */}
          <div className="absolute top-4 left-4 bg-gray-900/90 backdrop-blur rounded-lg p-3 z-[1000]">
            <div className="text-2xl font-bold text-orange-400">{dayData?.total_runs || 0}</div>
            <div className="text-sm text-gray-400">Total Runs</div>
          </div>
        </div>

        {/* Run List Panel - Right Side */}
        <div className="w-1/2 overflow-y-auto p-4 space-y-3">
          {dayData?.trucking?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <Truck className="h-16 w-16 mb-4 opacity-30" />
              <p className="text-xl">No runs scheduled</p>
              <p className="text-sm">for {format(parseISO(selectedDate), 'EEEE, MMMM d')}</p>
            </div>
          ) : (
            dayData?.trucking?.map((run, index) => {
              const travelKey = `${run.location_lat},${run.location_lon}`;
              const travelTime = travelTimes[travelKey];
              const leaveBy = isToday && travelTime?.duration_minutes 
                ? calculateLeaveBy(run.time, run.date, travelTime.duration_minutes)
                : null;
              const driver = run.truck_assignments?.[0];
              const isNextRun = nextRun?.id === run.id;
              const isSelected = selectedRunId === run.id;
              
              return (
                <div
                  key={run.id}
                  onClick={() => setSelectedRunId(run.id)}
                  className={`rounded-xl p-4 cursor-pointer transition-all ${
                    isNextRun
                      ? 'bg-orange-500/20 border-2 border-orange-500 ring-2 ring-orange-500/30'
                      : isSelected
                      ? 'bg-gray-800 border-2 border-orange-400'
                      : run.status === 'Confirmed'
                      ? 'bg-emerald-900/20 border border-emerald-700/50 hover:border-emerald-500'
                      : 'bg-gray-800/50 border border-gray-700/50 hover:border-gray-500'
                  }`}
                >
                  {/* Next Up Badge */}
                  {isNextRun && (
                    <div className="flex items-center gap-2 text-orange-400 text-sm font-semibold mb-2">
                      <ArrowRight className="h-4 w-4" />
                      NEXT UP
                    </div>
                  )}
                  
                  {/* Header Row */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`text-2xl font-mono font-bold ${isNextRun ? 'text-orange-400' : 'text-white'}`}>
                        {formatTime(run.time)}
                      </div>
                      {run.status === 'Confirmed' ? (
                        <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-yellow-400" />
                      )}
                    </div>
                    
                    {/* Travel Info */}
                    {travelTime?.duration_minutes && (
                      <div className="text-right">
                        <div className="flex items-center gap-1 text-gray-400">
                          <Navigation className="h-4 w-4" />
                          <span className="font-medium">{travelTime.duration_minutes} min</span>
                        </div>
                        <div className="text-xs text-gray-500">{travelTime.distance_miles} miles</div>
                      </div>
                    )}
                  </div>

                  {/* Event Info */}
                  <div className="mb-3">
                    <div className="text-lg font-semibold text-white">
                      {run.event_name}
                      {run.flex_number && (
                        <span className="text-gray-400 ml-2">#{run.flex_number}</span>
                      )}
                    </div>
                    <div className="text-gray-400">{run.action}</div>
                  </div>

                  {/* Location */}
                  <div className="flex items-start gap-2 mb-3">
                    <MapPin className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-white">{run.location_name}</div>
                      {run.location_address && (
                        <div className="text-sm text-gray-400">
                          {run.location_address}
                          {run.location_city && `, ${run.location_city}`}
                        </div>
                      )}
                      {run.dock_notes && (
                        <div className="text-sm text-orange-400 mt-1">{run.dock_notes}</div>
                      )}
                    </div>
                  </div>

                  {/* Driver & Truck */}
                  {driver && (
                    <div className="flex items-center gap-2 mb-3 p-2 bg-gray-900/50 rounded-lg">
                      <User className="h-5 w-5 text-emerald-400" />
                      <span className="font-medium text-emerald-400">{driver.driver_name}</span>
                      {driver.truck && (
                        <>
                          <span className="text-gray-600">•</span>
                          <Truck className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-300">{driver.truck}</span>
                        </>
                      )}
                    </div>
                  )}

                  {/* Leave By Countdown */}
                  {leaveBy && (
                    <div className={`flex items-center gap-2 p-3 rounded-lg ${
                      leaveBy.isPast 
                        ? 'bg-red-900/30 border border-red-700' 
                        : leaveBy.isUrgent 
                          ? 'bg-red-900/30 border border-red-500 animate-pulse' 
                          : leaveBy.isSoon 
                            ? 'bg-yellow-900/30 border border-yellow-700'
                            : 'bg-gray-900/50 border border-gray-700'
                    }`}>
                      <Timer className={`h-5 w-5 ${
                        leaveBy.isPast || leaveBy.isUrgent ? 'text-red-400' : leaveBy.isSoon ? 'text-yellow-400' : 'text-gray-400'
                      }`} />
                      <div className={`font-medium ${
                        leaveBy.isPast || leaveBy.isUrgent ? 'text-red-400' : leaveBy.isSoon ? 'text-yellow-400' : 'text-gray-300'
                      }`}>
                        {leaveBy.isPast ? (
                          <span>Should have left at {leaveBy.time}</span>
                        ) : (
                          <span>
                            Leave by <span className="font-bold">{leaveBy.time}</span>
                            <span className="text-gray-500 ml-2">({leaveBy.minutesUntil} min)</span>
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* CSS for pulse animation */}
      <style>{`
        @keyframes pulse-ring {
          0% { box-shadow: 0 0 0 0 rgba(249, 115, 22, 0.7); }
          70% { box-shadow: 0 0 0 15px rgba(249, 115, 22, 0); }
          100% { box-shadow: 0 0 0 0 rgba(249, 115, 22, 0); }
        }
        .pulse-marker > div {
          animation: pulse-ring 2s infinite;
        }
      `}</style>
    </div>
  );
};

export default DailyDispatchDisplay;
